/**
 * MeetingReview — the organizer's editor for a meeting's drafted notes.
 *
 * Left: the summary and the items, grouped in the sections the notes print
 * in. Every change saves as soon as a field loses focus (or a section is
 * picked); there is no separate Save. Each item shows when in the meeting it
 * came from and who was speaking, never transcript text. Items the model was
 * unsure of carry "Check this".
 * Right: the notes PDF exactly as it will be stored, refreshed after each save.
 * Footer: add to the company knowledge base, or discard the draft.
 *
 * Spec: docs/superpowers/specs/2026-10-06-teams-meeting-review-and-index-design.md
 */

import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import {
  addDraftItem,
  approveMeeting,
  discardMeeting,
  fetchDraftPdf,
  getMeetingDraft,
  removeDraftItem,
  updateDraftItem,
  updateDraftSummary,
  type DraftItem,
  type DraftItemChange,
  type MeetingDraft,
  type NoteCategory,
} from "@/api/meetingsApi";
import { useNotificationsOptional } from "@/notifications/NotificationsProvider";

const MAX_SUMMARY = 1000;

/** Same order and grouping as the PDF (libraries/meetings/notes.py). */
const SECTIONS: { title: string; categories: (NoteCategory | null)[] }[] = [
  { title: "Decisions", categories: ["decision"] },
  { title: "Action items", categories: ["action_item"] },
  { title: "Status", categories: ["status"] },
  { title: "Facts & figures", categories: ["fact"] },
  { title: "Requirements", categories: ["requirement"] },
  { title: "Risks & open questions", categories: ["risk", "open_question"] },
  { title: "Feedback", categories: ["feedback"] },
  { title: "Options rejected", categories: ["rejected_option"] },
  { title: "Procedures", categories: ["procedure"] },
  { title: "Other notes", categories: [null] },
];

const CATEGORY_LABEL: Record<NoteCategory, string> = {
  decision: "Decision",
  action_item: "Action item",
  status: "Status",
  fact: "Fact or figure",
  requirement: "Requirement",
  risk: "Risk",
  open_question: "Open question",
  feedback: "Feedback",
  rejected_option: "Option rejected",
  procedure: "Procedure",
};
const NO_CATEGORY = "";

const message = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong. Try again.");

function sourceLine(item: DraftItem): string | null {
  const parts = [item.time, item.speakers.join(", ")].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return item.source === "manual" ? "Added by you" : null;
}

function CategorySelect({
  value,
  onChange,
  label = "Section",
}: {
  value: NoteCategory | null;
  onChange: (value: NoteCategory | null) => void;
  label?: string;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value ?? NO_CATEGORY}
      onChange={(e) => onChange((e.target.value || null) as NoteCategory | null)}
      sx={{ minWidth: 170 }}
    >
      {(Object.keys(CATEGORY_LABEL) as NoteCategory[]).map((c) => (
        <MenuItem key={c} value={c}>
          {CATEGORY_LABEL[c]}
        </MenuItem>
      ))}
      <MenuItem value={NO_CATEGORY}>Other</MenuItem>
    </TextField>
  );
}

function ItemEditor({
  item,
  onSave,
  onRemove,
}: {
  item: DraftItem;
  onSave: (change: DraftItemChange) => Promise<boolean>;
  onRemove: () => Promise<void>;
}) {
  const [text, setText] = React.useState(item.text);
  const [owner, setOwner] = React.useState(item.owner ?? "");
  const [due, setDue] = React.useState(item.due_date ?? "");
  const [removing, setRemoving] = React.useState(false);

  const saveText = async () => {
    const next = text.trim();
    if (!next) {
      setText(item.text); // an empty item is removed, not blanked
      return;
    }
    if (next !== item.text && !(await onSave({ text: next }))) setText(item.text);
  };
  const saveOwner = async () => {
    const next = owner.trim() || null;
    if (next !== item.owner && !(await onSave({ owner: next }))) setOwner(item.owner ?? "");
  };
  const saveDue = async () => {
    const next = due || null;
    if (next !== item.due_date && !(await onSave({ due_date: next }))) setDue(item.due_date ?? "");
  };
  const source = sourceLine(item);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextField
          fullWidth
          multiline
          size="small"
          value={text}
          slotProps={{ htmlInput: { "aria-label": "Item text", maxLength: 2000 } }}
          onChange={(e) => setText(e.target.value)}
          onBlur={saveText}
        />
        <Tooltip title="Remove from the notes">
          <span>
            <IconButton
              aria-label="Remove item"
              size="small"
              disabled={removing}
              onClick={async () => {
                setRemoving(true);
                try {
                  await onRemove();
                } finally {
                  setRemoving(false);
                }
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mt: 1 }}>
        <CategorySelect value={item.category} onChange={(category) => void onSave({ category })} />
        {item.category === "action_item" && (
          <>
            <TextField
              size="small"
              label="Owner"
              value={owner}
              slotProps={{ htmlInput: { maxLength: 120 } }}
              onChange={(e) => setOwner(e.target.value)}
              onBlur={saveOwner}
            />
            <TextField
              size="small"
              type="date"
              label="Due date"
              value={due}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setDue(e.target.value)}
              onBlur={saveDue}
            />
          </>
        )}
        {item.low_confidence && <Chip size="small" color="warning" variant="outlined" label="Check this" />}
        {source && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
            {source}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function NewItemForm({
  onAdd,
  onCancel,
}: {
  onAdd: (text: string, category: NoteCategory | null) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [text, setText] = React.useState("");
  const [category, setCategory] = React.useState<NoteCategory | null>(null);
  const [busy, setBusy] = React.useState(false);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <TextField
        fullWidth
        multiline
        size="small"
        label="New item"
        value={text}
        slotProps={{ htmlInput: { maxLength: 2000 } }}
        onChange={(e) => setText(e.target.value)}
      />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
        <CategorySelect value={category} onChange={setCategory} />
        <Box sx={{ flex: 1 }} />
        <Button size="small" color="inherit" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={busy || !text.trim()}
          onClick={async () => {
            setBusy(true);
            const ok = await onAdd(text.trim(), category);
            setBusy(false);
            if (ok) onCancel();
          }}
        >
          Save item
        </Button>
      </Box>
    </Paper>
  );
}

function NotesPreview({ meetingId, version, delayMs }: { meetingId: string; version: number; delayMs: number }) {
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    // Debounced: a burst of edits renders one PDF, not one per keystroke-blur.
    const timer = setTimeout(() => {
      fetchDraftPdf(meetingId)
        .then((blob) => {
          if (cancelled) return;
          created = URL.createObjectURL(blob);
          setUrl(created);
          setError(null);
        })
        .catch((e) => !cancelled && setError(message(e)));
    }, delayMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      // The previous blob stays on screen until the next one is ready.
      if (created) setTimeout(() => URL.revokeObjectURL(created as string), 0);
    };
  }, [meetingId, version, delayMs]);

  if (error) return <Alert severity="warning">Preview unavailable: {error}</Alert>;
  if (!url) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  return (
    <Box
      component="iframe"
      title="Notes preview"
      src={url}
      sx={{ width: "100%", height: "100%", minHeight: 520, border: 0, borderRadius: 1 }}
    />
  );
}

export function MeetingReview({
  meetingId,
  onBack,
  onDone,
  previewDelayMs = 700,
}: {
  meetingId: string;
  onBack: () => void;
  onDone: (outcome: "approved" | "discarded") => void;
  previewDelayMs?: number;
}) {
  const notifications = useNotificationsOptional();
  const [draft, setDraft] = React.useState<MeetingDraft | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [summary, setSummary] = React.useState("");
  const [version, setVersion] = React.useState(0);
  const [adding, setAdding] = React.useState(false);
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [finishing, setFinishing] = React.useState<"approve" | "discard" | null>(null);

  const load = React.useCallback(() => {
    setLoadError(null);
    getMeetingDraft(meetingId)
      .then((d) => {
        setDraft(d);
        setSummary(d.summary ?? "");
      })
      .catch((e) => setLoadError(message(e)));
  }, [meetingId]);

  React.useEffect(load, [load]);

  const fail = (e: unknown) => notifications?.notify(message(e), "error");
  const saved = () => setVersion((v) => v + 1);

  const saveSummary = async () => {
    if (!draft || summary.trim() === (draft.summary ?? "")) return;
    try {
      await updateDraftSummary(meetingId, summary.trim());
      setDraft({ ...draft, summary: summary.trim() });
      saved();
    } catch (e) {
      fail(e);
    }
  };

  const saveItem = (item: DraftItem) => async (change: DraftItemChange) => {
    try {
      await updateDraftItem(meetingId, item.id, change);
      setDraft((d) =>
        d && {
          ...d,
          items: d.items.map((i) =>
            i.id === item.id ? { ...i, ...change, edited: i.edited || "text" in change } : i,
          ),
        },
      );
      saved();
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };

  const removeItem = (item: DraftItem) => async () => {
    try {
      await removeDraftItem(meetingId, item.id);
      setDraft((d) => d && { ...d, items: d.items.filter((i) => i.id !== item.id) });
      saved();
    } catch (e) {
      fail(e);
    }
  };

  const addItem = async (text: string, category: NoteCategory | null) => {
    try {
      await addDraftItem(meetingId, { text, category });
      load();
      saved();
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };

  const finish = async (which: "approve" | "discard") => {
    setFinishing(which);
    try {
      if (which === "approve") {
        await approveMeeting(meetingId);
        onDone("approved");
      } else {
        await discardMeeting(meetingId);
        onDone("discarded");
      }
    } catch (e) {
      fail(e);
    } finally {
      setFinishing(null);
      setConfirmDiscard(false);
    }
  };

  if (loadError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={load}>
            Retry
          </Button>
        }
      >
        {loadError}
      </Alert>
    );
  }
  if (!draft) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const editable = draft.status === "awaiting_review";
  const sections = SECTIONS.map((s) => ({
    ...s,
    items: draft.items.filter((i) => s.categories.includes(i.category)),
  })).filter((s) => s.items.length > 0);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <IconButton aria-label="Back to the list" size="small" onClick={onBack}>
          <ArrowLeft size={18} />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
          {draft.subject || "Untitled Teams meeting"}
        </Typography>
      </Box>

      {draft.last_error === "indexing_failed" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn't add these notes last time. Check them and try again.
        </Alert>
      )}
      {!editable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These notes are being added to the knowledge base and can no longer be edited here.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <TextField
            label="Summary"
            multiline
            minRows={2}
            value={summary}
            disabled={!editable}
            slotProps={{ htmlInput: { maxLength: MAX_SUMMARY } }}
            helperText={`${summary.length}/${MAX_SUMMARY}`}
            onChange={(e) => setSummary(e.target.value)}
            onBlur={saveSummary}
          />

          {sections.map((s) => (
            <Box key={s.title} data-testid={`section-${s.title}`}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {s.title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {s.items.map((i) => (
                  <ItemEditor key={i.id} item={i} onSave={saveItem(i)} onRemove={removeItem(i)} />
                ))}
              </Box>
            </Box>
          ))}

          {editable &&
            (adding ? (
              <NewItemForm onAdd={addItem} onCancel={() => setAdding(false)} />
            ) : (
              <Button
                startIcon={<Plus size={16} />}
                sx={{ alignSelf: "flex-start" }}
                onClick={() => setAdding(true)}
              >
                Add an item
              </Button>
            ))}
        </Box>

        <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, minHeight: 540, position: { md: "sticky" }, top: { md: 16 } }}>
          <NotesPreview meetingId={meetingId} version={version} delayMs={previewDelayMs} />
        </Paper>
      </Box>

      {editable && (
        <Paper
          variant="outlined"
          sx={{ mt: 2, p: 1.5, borderRadius: 2, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
            Visible to everyone in your company once added.
          </Typography>
          {confirmDiscard ? (
            <>
              <Typography variant="body2">Discard this draft? Nothing from the meeting is kept.</Typography>
              <Button size="small" color="inherit" onClick={() => setConfirmDiscard(false)} disabled={!!finishing}>
                Keep it
              </Button>
              <Button size="small" color="error" variant="contained" onClick={() => finish("discard")} disabled={!!finishing}>
                {finishing === "discard" ? <CircularProgress size={13} /> : "Discard draft"}
              </Button>
            </>
          ) : (
            <>
              <Button size="small" color="inherit" onClick={() => setConfirmDiscard(true)} disabled={!!finishing}>
                Discard
              </Button>
              <Button size="small" variant="contained" onClick={() => finish("approve")} disabled={!!finishing}>
                {finishing === "approve" ? <CircularProgress size={13} /> : "Add to knowledge base"}
              </Button>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
